import api from './api';

// Public menu endpoints
const getPublicMenu = async (merchantId) => {
  try {
    const response = await api.get(`/menus/public/menu/`, {
      params: { merchant: merchantId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching public menu:', error);
    throw error;
  }
};

const getPublicTableMenu = async (merchantId, tableId) => {
  try {
    const response = await api.get(`/menus/public/table-menu/`, {
      params: { merchant: merchantId, table: tableId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching public table menu:', error);
    throw error;
  }
};

const getPublicFeaturedItems = async (merchantId) => {
  try {
    const response = await api.get(`/menus/public/featured/`, {
      params: { merchant: merchantId }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching public featured items:', error);
    throw error;
  }
};

// Admin menu endpoints
const getMenuCategories = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/menus/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching menu categories:', error);
    throw error;
  }
};

const getMenuCategory = async (id) => {
  try {
    const response = await api.get(`/menus/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching menu category ${id}:`, error);
    throw error;
  }
};

const createMenuCategory = async (data) => {
  try {
    const response = await api.post('/menus/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating menu category:', error);
    throw error;
  }
};

const updateMenuCategory = async (id, data) => {
  try {
    const response = await api.put(`/menus/${id}/`, data);
    return response.data;
  } catch (error) {
    console.error(`Error updating menu category ${id}:`, error);
    throw error;
  }
};

const deleteMenuCategory = async (id) => {
  try {
    const response = await api.delete(`/menus/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting menu category ${id}:`, error);
    throw error;
  }
};

const getMenuItems = async (params = {}) => {
  try {
    // The merchant is automatically determined from the current logged-in user
    const response = await api.get('/menus/items/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching menu items:', error);
    throw error;
  }
};

const getMenuItem = async (id) => {
  try {
    const response = await api.get(`/menus/items/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching menu item ${id}:`, error);
    throw error;
  }
};

const createMenuItem = async (data) => {
  try {
    // Handle file uploads with FormData
    const formData = new FormData();

    // Append all fields to FormData
    Object.keys(data).forEach(key => {
      if (key === 'image' && data[key] instanceof File) {
        // Only append image if it's a File object
        formData.append(key, data[key]);
      } else if (key === 'image' && (data[key] === null || data[key] === undefined)) {
        // Skip null/undefined image values entirely
        return;
      } else if (typeof data[key] !== 'undefined' && data[key] !== null) {
        // Handle boolean values properly
        if (typeof data[key] === 'boolean') {
          formData.append(key, data[key] ? 'true' : 'false');
        } else if (typeof data[key] === 'object') {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    const response = await api.post('/menus/items/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error creating menu item:', error);
    throw error;
  }
};

const updateMenuItem = async (id, data) => {
  try {
    // Handle file uploads with FormData
    const formData = new FormData();

    // Append all fields to FormData
    Object.keys(data).forEach(key => {
      if (key === 'image' && data[key] instanceof File) {
        // Only append image if it's a File object (new upload)
        formData.append(key, data[key]);
      } else if (key === 'image' && data[key] === null) {
        // Send empty string to remove the image
        formData.append(key, '');
      } else if (key === 'image' && data[key] === undefined) {
        // Skip undefined image values entirely - don't send them to backend
        return;
      } else if (typeof data[key] !== 'undefined' && data[key] !== null) {
        // Handle boolean values properly
        if (typeof data[key] === 'boolean') {
          formData.append(key, data[key] ? 'true' : 'false');
        } else if (typeof data[key] === 'object') {
          formData.append(key, JSON.stringify(data[key]));
        } else {
          formData.append(key, data[key]);
        }
      }
    });

    const response = await api.put(`/menus/items/${id}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error) {
    console.error(`Error updating menu item ${id}:`, error);
    throw error;
  }
};

const deleteMenuItem = async (id) => {
  try {
    const response = await api.delete(`/menus/items/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting menu item ${id}:`, error);
    throw error;
  }
};

const toggleMenuItemFeatured = async (id) => {
  try {
    const response = await api.post(`/menus/items/${id}/toggle-featured/`);
    return response.data;
  } catch (error) {
    console.error(`Error toggling featured status for menu item ${id}:`, error);
    throw error;
  }
};

const toggleMenuCategoryAvailability = async (id) => {
  try {
    const response = await api.post(`/menus/${id}/toggle-availability/`);
    return response.data;
  } catch (error) {
    console.error(`Error toggling availability for category ${id}:`, error);
    throw error;
  }
};

// Option Group endpoints
const getOptionGroups = async (params = {}) => {
  const response = await api.get('/menus/option-groups/', { params });
  return response.data;
};

const getOptionGroup = async (id) => {
  const response = await api.get(`/menus/option-groups/${id}/`);
  return response.data;
};

const createOptionGroup = async (data) => {
  const response = await api.post('/menus/option-groups/', data);
  return response.data;
};

const updateOptionGroup = async (id, data) => {
  const response = await api.put(`/menus/option-groups/${id}/`, data);
  return response.data;
};

const deleteOptionGroup = async (id) => {
  const response = await api.delete(`/menus/option-groups/${id}/`);
  return response.data;
};

// Option Group Item endpoints
const getOptionGroupItems = async (groupId, params = {}) => {
  const response = await api.get(`/menus/option-groups/${groupId}/items/`, { params });
  return response.data;
};

const createOptionGroupItem = async (groupId, data) => {
  const response = await api.post(`/menus/option-groups/${groupId}/items/`, data);
  return response.data;
};

const updateOptionGroupItem = async (id, data) => {
  const response = await api.put(`/menus/option-group-items/${id}/`, data);
  return response.data;
};

const deleteOptionGroupItem = async (id) => {
  const response = await api.delete(`/menus/option-group-items/${id}/`);
  return response.data;
};

// Menu Item <-> Option Group linking
const getMenuItemOptionGroups = async (menuItemId) => {
  const response = await api.get(`/menus/items/${menuItemId}/option-groups/`);
  return response.data;
};

const linkOptionGroupToMenuItem = async (menuItemId, data) => {
  const response = await api.post(`/menus/items/${menuItemId}/option-groups/`, data);
  return response.data;
};

const unlinkOptionGroupFromMenuItem = async (menuItemId, optionGroupId) => {
  const response = await api.delete(`/menus/items/${menuItemId}/option-groups/`, {
    params: { option_group_id: optionGroupId }
  });
  return response.data;
};

const reorderMenuCategories = async (orderedIds) => {
  const response = await api.post('/menus/reorder/', { ordered_ids: orderedIds });
  return response.data;
};

const menuService = {
  // Public endpoints
  getPublicMenu,
  getPublicTableMenu,
  getPublicFeaturedItems,

  // Admin endpoints
  getMenuCategories,
  getMenuCategory,
  createMenuCategory,
  updateMenuCategory,
  deleteMenuCategory,
  reorderMenuCategories,

  getMenuItems,
  getMenuItem,
  createMenuItem,
  updateMenuItem,
  deleteMenuItem,
  toggleMenuItemFeatured,
  toggleMenuCategoryAvailability,

  getOptionGroups,
  getOptionGroup,
  createOptionGroup,
  updateOptionGroup,
  deleteOptionGroup,

  getOptionGroupItems,
  createOptionGroupItem,
  updateOptionGroupItem,
  deleteOptionGroupItem,

  getMenuItemOptionGroups,
  linkOptionGroupToMenuItem,
  unlinkOptionGroupFromMenuItem,
};

export default menuService;
